import { useState, useEffect } from 'react';
import { ImageUploader } from '@/components/ImageUploader';
import { toast } from 'sonner';
import Header from '@/components/Header';
import ProcessingProgress from '@/components/ProcessingProgress';
import ImageControls from '@/components/ImageControls';
import BatchModeControls from '@/components/BatchModeControls';
import Footer from '@/components/Footer';
import HomeContent from '@/components/HomeContent';
import Seo from '@/components/Seo';
import { getGridDimensions, loadImage } from '@/utils/imageProcessing';
import { initializeAnalytics, trackImageCombination, trackDownload } from '@/utils/analytics';
import { useIsMobile } from '@/hooks/use-mobile';
import Navbar from '@/components/Navbar';
import JSZip from 'jszip';

const Index = () => {
  const [images, setImages] = useState<File[]>([]);
  const [combinedImageUrl, setCombinedImageUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileFormat, setFileFormat] = useState<'png' | 'jpeg' | 'webp'>('jpeg');
  const [resetKey, setResetKey] = useState(0);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [imagesPerCollage, setImagesPerCollage] = useState(2);
  const [batchCollages, setBatchCollages] = useState<string[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    initializeAnalytics();
  }, []);

  const createSingleCollage = async (imagesToCombine: File[]): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');

    const loadedImages = await Promise.all(imagesToCombine.map(loadImage));
    const [cols, rows] = getGridDimensions(imagesToCombine.length);
    
    const maxCellWidth = Math.max(...loadedImages.map(img => img.width));
    const maxCellHeight = Math.max(...loadedImages.map(img => img.height));

    canvas.width = maxCellWidth * cols;
    canvas.height = maxCellHeight * rows;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    loadedImages.forEach((img, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      
      const x = col * maxCellWidth;
      const y = row * maxCellHeight;
      
      const scale = Math.min(
        maxCellWidth / img.width,
        maxCellHeight / img.height
      );
      
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const xOffset = x + (maxCellWidth - scaledWidth) / 2;
      const yOffset = y + (maxCellHeight - scaledHeight) / 2;
      
      ctx.drawImage(img, xOffset, yOffset, scaledWidth, scaledHeight);
    });

    // Add watermark with relative sizing and rounded background
    const watermarkText = 'Created with CardCombiner.com';
    const minDimension = Math.min(canvas.width, canvas.height);
    const fontSize = Math.max(minDimension * 0.02, 16);
    const padding = fontSize * 0.8;

    ctx.font = `${fontSize}px Arial`;
    const watermarkWidth = ctx.measureText(watermarkText).width;
    const watermarkHeight = fontSize;

    const borderRadius = fontSize * 0.3;
    const margin = fontSize * 0.5;
    const x = canvas.width - watermarkWidth - padding * 2 - margin;
    const y = canvas.height - watermarkHeight - padding - margin;
    const width = watermarkWidth + padding * 2;
    const height = watermarkHeight + padding;

    ctx.beginPath();
    ctx.moveTo(x + borderRadius, y);
    ctx.lineTo(x + width - borderRadius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + borderRadius);
    ctx.lineTo(x + width, y + height - borderRadius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - borderRadius, y + height);
    ctx.lineTo(x + borderRadius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - borderRadius);
    ctx.lineTo(x, y + borderRadius);
    ctx.quadraticCurveTo(x, y, x + borderRadius, y);
    ctx.closePath();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fill();

    ctx.fillStyle = '#221F26';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(
      watermarkText,
      x + width / 2,
      y + height / 2
    );

    const mimeType = `image/${fileFormat}`;
    return canvas.toDataURL(mimeType);
  };

  const combineImages = async () => {
    if (images.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    setIsProcessing(true);
    setCombinedImageUrl(null);
    setBatchCollages([]);

    try {
      if (isBatchMode) {
        // Batch mode: create multiple collages
        const collageCount = Math.floor(images.length / imagesPerCollage);
        const collages: string[] = [];

        for (let i = 0; i < collageCount; i++) {
          const startIdx = i * imagesPerCollage;
          const endIdx = startIdx + imagesPerCollage;
          const batchImages = images.slice(startIdx, endIdx);
          const collageUrl = await createSingleCollage(batchImages);
          collages.push(collageUrl);
        }

        setBatchCollages(collages);
        toast.success(`${collages.length} collages created successfully`);
        trackImageCombination(images.length, fileFormat, true);
      } else {
        // Single mode: create one collage
        const collageUrl = await createSingleCollage(images);
        setCombinedImageUrl(collageUrl);
        toast.success('Images combined successfully');
        trackImageCombination(images.length, fileFormat, true);
      }
    } catch (error) {
      toast.error('Error combining images');
      console.error('Error combining images:', error);
      trackImageCombination(images.length, fileFormat, false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    const timestamp = `${year}${month}${day}-${hours}:${minutes}:${seconds}`;

    try {
      if (isBatchMode && batchCollages.length > 0) {
        // Batch mode: create zip file with all collages
        const zip = new JSZip();
        
        for (let i = 0; i < batchCollages.length; i++) {
          const blob = await fetch(batchCollages[i]).then(r => r.blob());
          zip.file(`collage_${i + 1}.${fileFormat}`, blob);
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipUrl = URL.createObjectURL(zipBlob);
        
        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = `CardCombiner_Batch_${timestamp}.zip`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => URL.revokeObjectURL(zipUrl), 1000);
        toast.success(`${batchCollages.length} collages downloaded! Ready for new images`);
      } else if (combinedImageUrl) {
        // Single mode: download single image
        const filename = `CardCombiner_${timestamp}.${fileFormat}`;
        const blob = await fetch(combinedImageUrl).then(r => r.blob());
        const blobUrl = URL.createObjectURL(blob);
        
        // Detect iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
        
        // Check if download attribute is supported
        const supportsDownload = 'download' in document.createElement('a');
        
        if (isIOS && !supportsDownload) {
          // iOS fallback - open in new window with instructions
          const newWindow = window.open(blobUrl, '_blank');
          if (newWindow) {
            toast.success('Image opened in new tab. Long press the image and select "Save to Photos"');
          } else {
            toast.error('Please allow popups to download on iOS');
          }
        } else {
          // Standard download approach
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('Download complete! Ready for new images');
        }
        
        // Clean up blob URL
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
      
      trackDownload(fileFormat);
      
      // Clear all state to reset the app
      setImages([]);
      setCombinedImageUrl(null);
      setBatchCollages([]);
      setIsProcessing(false);
      setFileFormat('jpeg');
      setResetKey(prev => prev + 1);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const handleImagesSelect = (selectedImages: File[]) => {
    setImages(selectedImages);
  };

  useEffect(() => {
    if (images.length > 0) {
      combineImages();
    }
  }, [images, fileFormat, isBatchMode, imagesPerCollage]);

  return (
    <div className="min-h-screen bg-[#f1f1f1] flex flex-col">
      <Seo
        title="CardCombiner – Free Online Image Grid & Collage Maker"
        description="Combine up to 9 images into one clean grid in seconds. Free, private, no sign-up. Built for trading-card sellers, marketplace listings, and quick collages."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "CardCombiner",
          url: "https://cardcombiner.com/",
          applicationCategory: "MultimediaApplication",
          operatingSystem: "Any",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />
      <Navbar />
      <div className="max-w-4xl mx-auto p-4 md:p-8 flex-grow">
        <Header />

        <div className="mb-6 animate-slide-up">
          <BatchModeControls
            isBatchMode={isBatchMode}
            onBatchModeChange={setIsBatchMode}
            imagesPerCollage={imagesPerCollage}
            onImagesPerCollageChange={setImagesPerCollage}
            totalImages={images.length}
          />
        </div>

        <div className="mb-8 md:mb-12 animate-slide-up">
          <ImageUploader 
            key={resetKey} 
            onImagesSelect={handleImagesSelect} 
            label="Select Your Images"
            isBatchMode={isBatchMode}
          />
        </div>

        <ProcessingProgress isProcessing={isProcessing} />

        <div className="space-y-4 md:space-y-6 animate-fade-in">
          <ImageControls
            fileFormat={fileFormat}
            onFormatChange={setFileFormat}
            onDownload={handleDownload}
            hasFiles={(combinedImageUrl !== null || batchCollages.length > 0) && !isProcessing}
            isMobile={isMobile}
          />

          {batchCollages.length > 0 && (
            <div className="bg-white p-4 md:p-8 rounded-[16px] md:rounded-[24px] shadow-lg border-2 md:border-4 border-[#9B87F5]">
              <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-[#6B4EE6]">
                {batchCollages.length} Collages Ready
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {batchCollages.map((collageUrl, index) => (
                  <div key={index}>
                    <p className="text-sm font-medium text-[#6B4EE6] mb-2">Collage {index + 1}</p>
                    <img
                      src={collageUrl}
                      alt={`Collage ${index + 1}`}
                      className="w-full rounded-[12px] md:rounded-[20px] shadow-md"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {combinedImageUrl && !isBatchMode && (
            <div className="bg-white p-4 md:p-8 rounded-[16px] md:rounded-[24px] shadow-lg border-2 md:border-4 border-[#9B87F5]">
              <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-[#6B4EE6]">
                Combined Image
              </h2>
              <img
                src={combinedImageUrl}
                alt="Combined image"
                className="w-full rounded-[12px] md:rounded-[20px] shadow-md"
              />
            </div>
          )}
        </div>

        <HomeContent />
      </div>
      <Footer />
    </div>
  );
};

export default Index;
